import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";

interface AddMoleculeModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  pageNumber: number;
  onSubmit: (smiles: string) => Promise<void>;
  mode?: "add" | "edit";
  initialSmiles?: string;
}

const AddMoleculeModal: React.FC<AddMoleculeModalProps> = ({
  isOpen,
  setIsOpen,
  pageNumber,
  onSubmit,
  mode = "add",
  initialSmiles = "",
}) => {
  const [smiles, setSmiles] = useState(initialSmiles);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSmiles(initialSmiles);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, initialSmiles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = smiles.trim();
    if (!trimmed) {
      setError("SMILES is required");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      setIsOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || "Failed to add molecule");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="w-full">
        <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
          {mode === "edit" ? "Edit molecule" : "Add molecule"} on page {pageNumber}
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          {mode === "edit"
            ? "Update the SMILES for this manually-added molecule."
            : "Manually attach a SMILES for a molecule that wasn't detected."}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="add-molecule-smiles" className="block text-sm font-medium text-gray-700 mb-1.5">
              SMILES
            </label>
            <input
              id="add-molecule-smiles"
              type="text"
              value={smiles}
              onChange={(e) => setSmiles(e.target.value)}
              placeholder="e.g. CC(=O)Oc1ccccc1C(=O)O"
              className="w-full h-10 px-3 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50"
              autoFocus
              disabled={isSubmitting}
              spellCheck={false}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <SecondaryButton
              type="button"
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={isSubmitting || !smiles.trim()}
              className="flex-1"
            >
              {isSubmitting
                ? mode === "edit"
                  ? "Saving..."
                  : "Adding..."
                : mode === "edit"
                  ? "Save changes"
                  : "Add molecule"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddMoleculeModal;
